import React, { useState } from "react";

import ContentQuestionAnswerForm from "./ContentQuestionAnswerForm";
import ContentQuestionAttachments from "./ContentQuestionAttachments";
import ContentQuestionReactions from "./ContentQuestionReactions";
import ContentQuestionTags from "./ContentQuestionTags";

const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};

const formatTime = (seconds) => {
    const value = Math.max(0, Number(seconds || 0));
    const minutes = Math.floor(value / 60);
    const remaining = Math.floor(value % 60);
    return `${minutes}:${String(remaining).padStart(2, "0")}`;
};

const getContextLabel = (question) => {
    if (question.videoTimestampSeconds != null) return formatTime(question.videoTimestampSeconds);
    if (question.pdfPage != null) return `Page ${question.pdfPage}`;
    if (question.scormNodeId || question.scormNodeTitle) return question.scormNodeTitle || question.scormNodeId;
    if (question.scrollPosition != null) return `Position ${question.scrollPosition}`;
    return null;
};

const getAuthorLabel = (item) => item?.authorFullName || item?.authorEmail || "Utilisateur";

export default function ContentQuestionThread({
    question,
    onAnswer,
    onResolve,
    onBestAnswer,
    onQuestionReaction,
    onAnswerReaction,
    onDownloadAttachment,
    onDeleteAttachment,
    onNavigateContext,
    submitting,
    moduleId,
    highlightedQuestionId,
    highlightedAnswerId,
}) {
    const [answerOpen, setAnswerOpen] = useState(false);
    const contextLabel = getContextLabel(question);
    const canResolve = question.canResolve === true;
    const canChooseBestAnswer = question.canChooseBestAnswer === true;
    const canReply = question.canReply !== false;

    const handleAnswer = async (payload) => {
        await onAnswer?.(question.id, payload);
        setAnswerOpen(false);
    };

    return (
        <article
            className={`cq-thread ${String(highlightedQuestionId) === String(question.id) ? "is-highlighted" : ""}`}
            id={`cq-question-${question.id}`}
        >
            <div className="cq-thread-header">
                <div>
                    <h4>{question.title}</h4>
                    <ContentQuestionTags tags={question.tags || []} />
                    <span className="cq-muted">Par {getAuthorLabel(question)} - {formatDate(question.createdAt)}</span>
                </div>
                <span className={`cq-badge ${question.resolved ? "cq-badge--resolved" : "cq-badge--open"}`}>
                    {question.resolved ? "Résolu" : "Non résolu"}
                </span>
            </div>

            <p className="cq-message">{question.message}</p>
            <ContentQuestionAttachments
                attachments={question.attachments || []}
                onDownload={onDownloadAttachment}
                onDelete={onDeleteAttachment}
                disabled={submitting}
            />

            <div className="cq-meta">
                {contextLabel ? (
                    <button type="button" className="cq-badge cq-context-button" onClick={() => onNavigateContext?.(question)}>
                        {contextLabel}
                    </button>
                ) : null}
                <span className="cq-badge">{question.answers?.length || 0} reponse(s)</span>
            </div>

            <ContentQuestionReactions
                item={question}
                onToggle={(reactionType) => onQuestionReaction?.(question.id, reactionType)}
                disabled={submitting}
            />

            <div className="cq-thread-actions">
                {canReply ? (
                    <button type="button" className="cq-btn" onClick={() => setAnswerOpen((open) => !open)}>
                        Répondre
                    </button>
                ) : null}
                {!question.resolved && canResolve ? (
                    <button type="button" className="cq-btn" onClick={() => onResolve?.(question.id)} disabled={submitting}>
                        Marquer résolu
                    </button>
                ) : null}
            </div>

            {answerOpen ? <ContentQuestionAnswerForm moduleId={moduleId} onSubmit={handleAnswer} submitting={submitting} /> : null}

            <div className="cq-answers">
                {(question.answers || []).map((answer) => (
                    <div
                        key={answer.id}
                        id={`cq-answer-${answer.id}`}
                        className={`cq-answer ${answer.isBestAnswer ? "cq-answer--best" : ""} ${String(highlightedAnswerId) === String(answer.id) ? "is-highlighted" : ""}`}
                    >
                        <div className="cq-answer-top">
                            <span>{getAuthorLabel(answer)} - {formatDate(answer.createdAt)}</span>
                            {answer.isBestAnswer ? <span className="cq-badge cq-badge--resolved">Meilleure reponse</span> : null}
                        </div>
                        <p className="cq-answer-message">{answer.message}</p>
                        <ContentQuestionAttachments
                            attachments={answer.attachments || []}
                            onDownload={onDownloadAttachment}
                            onDelete={onDeleteAttachment}
                            disabled={submitting}
                        />
                        <ContentQuestionReactions
                            item={answer}
                            onToggle={(reactionType) => onAnswerReaction?.(question.id, answer.id, reactionType)}
                            disabled={submitting}
                        />
                        {!answer.isBestAnswer && canChooseBestAnswer ? (
                            <button type="button" className="cq-btn" onClick={() => onBestAnswer?.(question.id, answer.id)} disabled={submitting}>
                                Definir meilleure reponse
                            </button>
                        ) : null}
                    </div>
                ))}
            </div>
        </article>
    );
}
